// Design tokens for the Menus workspace ONLY (AdvancedMenus + its tabs and
// their dialogs). Pure data — importing this file has zero side effects.
// Other admin pages keep the global theme; do not import this outside the
// Menus workspace. The MUI component overrides that go with these tokens live
// in menuWorkspaceTheme.js, which AdvancedMenus wraps around the workspace.

export const mw = {
  font: {
    display: '"Bricolage Grotesque", "Figtree", system-ui, sans-serif',
    body: '"Figtree", system-ui, -apple-system, "Segoe UI", sans-serif',
  },
  radius: { lg: '24px', card: '18px', md: '18px', panel: '14px', sm: '12px', xs: '10px', badge: '8px', pill: '999px' },
  shadow: {
    card: '0 1px 2px rgba(23,24,30,0.04), 0 8px 24px -16px rgba(23,24,30,0.12)',
    lg: '0 1px 2px rgba(23,24,30,0.04), 0 8px 24px -16px rgba(23,24,30,0.12)',
    pop: '0 18px 40px -12px rgba(23,24,30,0.24), 0 2px 6px rgba(23,24,30,0.06)',
    cta: '0 6px 16px -6px rgba(194,65,12,0.55)',
    pill: '0 1px 2px rgba(23,24,30,0.06), 0 4px 10px -4px rgba(23,24,30,0.14)',
  },
  color: {
    accent: '#C2410C',
    accentDark: '#A9380A',
    accentSoft: '#FDEEE6',
    accentBorder: '#F4C7AE',
    cta: '#C2410C',
    ctaDark: '#A9380A',
    pageBg: '#F5F3EE',
    cardBg: '#FFFFFF',
    headBg: '#FAF8F4',
    subtleBg: '#F1EEE8',
    hoverBg: '#FCFBF8',
    border: '#E6E2DA',
    borderStrong: '#D6D1C6',
    divider: '#F0EDE7',
    hairline: '#F0EDE7',
    segmentBg: '#EBE7DF',
    countIdle: '#DCD7CD',
    textPrimary: '#17181E',
    textSecondary: '#45454D',
    textMuted: '#67666E',
    textFaint: '#8B8A92',
    on: '#2E8A4C',
    onText: '#166534',
    off: '#B91C1C',
    danger: '#B91C1C',
    dangerSoft: '#FDECEC',
    dangerBorder: '#F3C9C9',
    ai: '#3F3AC4',
    aiDark: '#322DA6',
    aiSoft: '#EEEDFC',
    aiBorder: '#DCDAF8',
    emerald: '#166534',
    emeraldSoft: '#E6F4EA',
    emeraldBorder: '#CBE7D3',
    amber: '#8A3B0B',
    amberSoft: '#FDF0E1',
    amberBorder: '#F5D9B8',
    sky: '#1D5B85',
    skySoft: '#E7F1F8',
    skyBorder: '#C9DFEE',
    scrim: 'rgba(62,61,70,0.85)',
  },
}

// Numbers, prices and counts line up in columns.
export const numSx = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 }

// Display face: page title, dialog titles, stat numbers.
export const displaySx = (size = 26) => ({
  fontFamily: mw.font.display,
  fontWeight: 700,
  fontSize: size,
  lineHeight: 1.1,
  letterSpacing: size >= 30 ? '-0.025em' : '-0.015em',
  color: mw.color.textPrimary,
})

// Small uppercase label above a title ("EDIT ITEM").
export const eyebrowSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: mw.color.accent,
  lineHeight: 1.4,
}

// ---- Top-level workspace tabs: a segmented control ----
// The grey track is drawn on the tab row itself (not the Tabs root), so the
// root keeps its full width and MUI's scroll detection stays accurate; on a
// phone the track scrolls sideways inside the gutter.
export const segmentedTabsSx = {
  minHeight: 0,
  '& .MuiTabs-flexContainer': {
    display: 'inline-flex',
    verticalAlign: 'top',
    gap: '4px',
    p: '5px',
    bgcolor: mw.color.segmentBg,
    borderRadius: '14px',
  },
  // The indicator is the white pill; it slides between tabs.
  '& .MuiTabs-indicator': {
    top: '5px',
    bottom: 'auto',
    height: 'calc(100% - 10px)',
    borderRadius: '10px',
    bgcolor: '#fff',
    boxShadow: mw.shadow.pill,
    zIndex: 0,
  },
  '& .MuiTab-root': {
    zIndex: 1,
    minHeight: 40,
    minWidth: 0,
    px: 2,
    py: 0,
    borderRadius: '10px',
    textTransform: 'none',
    fontFamily: mw.font.body,
    fontWeight: 600,
    fontSize: 14.5,
    color: mw.color.textMuted,
    transition: 'color .15s ease',
    '&:hover': { color: mw.color.textPrimary },
    '&.Mui-selected': { color: mw.color.textPrimary },
  },
  '& .MuiTabs-scrollButtons': { width: 32, borderRadius: '10px', '&.Mui-disabled': { opacity: 0 } },
}

// Count badge inside a tab label; orange on the active tab.
export const countBadgeSx = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 22,
  height: 22,
  px: '7px',
  ml: 1,
  borderRadius: mw.radius.pill,
  fontSize: 12,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  bgcolor: active ? mw.color.accent : mw.color.countIdle,
  color: active ? '#fff' : mw.color.textSecondary,
  transition: 'background-color .15s ease, color .15s ease',
})

// ---- Underline tab bars (Modifier Studio sub-tabs + ItemForm inner tabs).
//      Beats the global MuiTab #1976d2 override. ----
export const tabsSx = {
  minHeight: 0,
  '& .MuiTab-root': {
    textTransform: 'none',
    fontFamily: mw.font.body,
    fontWeight: 600,
    fontSize: 14.5,
    minHeight: 46,
    minWidth: 0,
    px: 0.5,
    mr: 3,
    color: mw.color.textMuted,
    '&:hover': { color: mw.color.textPrimary },
    '&.Mui-selected': { color: mw.color.textPrimary },
  },
  '& .MuiTabs-indicator': {
    bgcolor: mw.color.accent,
    height: 2,
    borderRadius: '2px 2px 0 0',
  },
}

// Count pill next to an underline tab's label.
export const countPillSx = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 22,
  height: 20,
  px: '7px',
  ml: 1,
  borderRadius: mw.radius.pill,
  fontSize: 11.5,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  bgcolor: active ? mw.color.accentSoft : mw.color.subtleBg,
  color: active ? mw.color.accent : mw.color.textMuted,
})

// ---- Sticky toolbar (ItemsTab / CategoriesTab page title row) ----
// GEOMETRY IS LOAD-BEARING: the table header below pins at top: 72, so the
// toolbar is at least 72px tall (44px controls + 14px above and below) and its
// background must equal the page background.
export const stickyToolbarSx = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
  bgcolor: mw.color.pageBg,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: { xs: 'wrap', md: 'nowrap' },
  gap: 1.5,
  boxSizing: 'border-box',
  minHeight: 72,
  py: 1.75,
  mb: 1,
  '& .MuiTypography-h4': {
    fontFamily: mw.font.display,
    fontWeight: 700,
    fontSize: 24,
    letterSpacing: '-0.015em',
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
  },
}

// ---- Section title row inside a tab (Modifier Studio sub-tabs) ----
export const sectionHeaderSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1.5,
  mb: 2.5,
  '& .MuiTypography-h4': {
    fontFamily: mw.font.display,
    fontWeight: 700,
    fontSize: 24,
    letterSpacing: '-0.015em',
    lineHeight: 1.15,
    color: mw.color.textPrimary,
  },
}

// ---- Table pieces ----
// Sticky variants keep overflow visible (overflow hidden kills position:
// sticky on the th cells), so the top corners are rounded on the th's.
export const tableCardStickySx = {
  borderRadius: mw.radius.card,
  border: `1px solid ${mw.color.border}`,
  boxShadow: mw.shadow.card,
  overflow: 'visible',
  bgcolor: mw.color.cardBg,
  '& thead th:first-of-type': { borderTopLeftRadius: mw.radius.card },
  '& thead th:last-of-type': { borderTopRightRadius: mw.radius.card },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
}

export const tableCardSx = {
  borderRadius: mw.radius.card,
  border: `1px solid ${mw.color.border}`,
  boxShadow: mw.shadow.card,
  overflow: 'hidden',
  bgcolor: mw.color.cardBg,
}

export const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  color: mw.color.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  bgcolor: mw.color.headBg,
  borderBottom: `1px solid ${mw.color.border}`,
  whiteSpace: 'nowrap',
}

// ---- Filter / search ----
export const filterBarSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: mw.radius.card,
  border: `1px solid ${mw.color.border}`,
  boxShadow: mw.shadow.card,
  bgcolor: mw.color.cardBg,
}

export const searchFieldSx = {
  bgcolor: 'transparent',
  '& .MuiOutlinedInput-root': {
    height: 44,
    bgcolor: mw.color.cardBg,
    borderRadius: mw.radius.sm,
    fontSize: 14.5,
    '& fieldset': { borderColor: mw.color.border },
    '&:hover fieldset': { borderColor: mw.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: mw.color.accent, borderWidth: '1.5px' },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${mw.color.accentSoft}` },
  },
  '& .MuiInputAdornment-root svg': { fontSize: 19, color: mw.color.textFaint },
  '& input::placeholder': { color: mw.color.textFaint, opacity: 1 },
}

// ---- Buttons ----
// Height comes from the workspace theme: 44px for default-size buttons, 32px
// for size="small" — so the same look works for toolbar and inline buttons.
const btnBase = {
  borderRadius: mw.radius.sm,
  textTransform: 'none',
  fontFamily: mw.font.body,
  fontWeight: 600,
  fontSize: 14.5,
  px: 2.25,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'background-color .15s ease, border-color .15s ease, box-shadow .15s ease, color .15s ease',
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 19 },
}

// Secondary: white with a border.
export const pillBtnSx = {
  ...btnBase,
  bgcolor: mw.color.cardBg,
  border: `1px solid ${mw.color.border}`,
  borderColor: mw.color.border,
  color: mw.color.textPrimary,
  '&:hover': { bgcolor: mw.color.headBg, borderColor: mw.color.borderStrong },
  '&.Mui-disabled': { bgcolor: mw.color.cardBg, borderColor: mw.color.divider, color: mw.color.textFaint },
}

// Primary: solid orange.
export const pillBtnPrimarySx = {
  ...btnBase,
  bgcolor: mw.color.cta,
  color: '#fff',
  boxShadow: mw.shadow.cta,
  '&:hover': { bgcolor: mw.color.ctaDark, boxShadow: mw.shadow.cta },
  '&.Mui-disabled': { bgcolor: mw.color.accentBorder, color: '#fff', boxShadow: 'none' },
}

export const pillBtnDangerSx = {
  ...btnBase,
  bgcolor: mw.color.cardBg,
  border: `1px solid ${mw.color.dangerBorder}`,
  borderColor: mw.color.dangerBorder,
  color: mw.color.danger,
  '&:hover': { borderColor: mw.color.danger, bgcolor: mw.color.dangerSoft },
}

// AI actions: indigo-soft.
export const aiBtnSx = {
  ...btnBase,
  bgcolor: mw.color.aiSoft,
  border: `1px solid ${mw.color.aiBorder}`,
  borderColor: mw.color.aiBorder,
  color: mw.color.ai,
  '&:hover': { bgcolor: '#E4E2FB', borderColor: '#CBC8F5' },
  '&.Mui-disabled': { bgcolor: mw.color.aiSoft, borderColor: mw.color.aiBorder, color: '#9C99DF' },
}

// Row action icon buttons: muted, 36x36.
export const iconBtnSx = {
  width: 36,
  height: 36,
  borderRadius: mw.radius.xs,
  color: mw.color.textMuted,
  bgcolor: 'transparent',
  transition: 'background-color .15s ease, color .15s ease',
  '& svg': { fontSize: 19 },
  '&:hover': { bgcolor: mw.color.subtleBg, color: mw.color.textPrimary },
}

// Delete: muted until hovered, then red.
export const iconBtnDangerSx = {
  ...iconBtnSx,
  '&:hover': { bgcolor: mw.color.dangerSoft, color: mw.color.danger },
}

// "Add group" — a small dashed circular +.
export const addGroupBtnSx = {
  width: 26,
  height: 26,
  p: 0,
  borderRadius: '50%',
  border: `1.5px dashed ${mw.color.borderStrong}`,
  color: mw.color.textMuted,
  '& svg': { fontSize: 16 },
  '&:hover': { borderColor: mw.color.accent, color: mw.color.accent, bgcolor: mw.color.accentSoft },
  '&.Mui-disabled': { borderColor: mw.color.divider, color: mw.color.countIdle },
}

// ---- Chips: four visually distinct kinds + helpers ----
const chipBase = {
  height: 26,
  borderRadius: mw.radius.pill,
  fontSize: 12.5,
  fontWeight: 600,
  border: 'none',
  '& .MuiChip-label': { px: 1.25 },
}

export const chipSx = {
  modifier: { ...chipBase, bgcolor: mw.color.emeraldSoft, color: mw.color.emerald },
  option: { ...chipBase, bgcolor: mw.color.amberSoft, color: mw.color.amber },
  category: { ...chipBase, bgcolor: mw.color.subtleBg, color: mw.color.textSecondary },
  multiSize: { ...chipBase, bgcolor: mw.color.skySoft, color: mw.color.sky },
  ingredient: { ...chipBase, bgcolor: mw.color.aiSoft, color: mw.color.ai },
  // "+N more": dashed outline.
  overflow: {
    ...chipBase,
    bgcolor: 'transparent',
    color: mw.color.textMuted,
    border: `1px dashed ${mw.color.borderStrong}`,
  },
  // A removable chip: small round × inside the chip, red on hover.
  deleteIcon: {
    '& .MuiChip-label': { pl: 1.25, pr: 0.5 },
    '& .MuiChip-deleteIcon': {
      color: 'currentColor',
      opacity: 0.45,
      fontSize: 16,
      mr: '5px',
      ml: 0,
      transition: 'opacity .15s ease, color .15s ease',
      '&:hover': { color: mw.color.danger, opacity: 1 },
    },
  },
}

// ---- Badges (radius 8, not pills) ----
const badgeBase = {
  height: 26,
  borderRadius: mw.radius.badge,
  fontSize: 12.5,
  fontWeight: 600,
  border: 'none',
  '& .MuiChip-label': { px: 1.1 },
}
export const badgeSx = {
  required: { ...badgeBase, bgcolor: mw.color.amberSoft, color: mw.color.amber },
  optional: { ...badgeBase, bgcolor: mw.color.subtleBg, color: mw.color.textSecondary },
  neutral: { ...badgeBase, bgcolor: mw.color.subtleBg, color: mw.color.textSecondary, textTransform: 'capitalize' },
  multi: { ...badgeBase, bgcolor: mw.color.aiSoft, color: mw.color.ai },
  pizza: { ...badgeBase, bgcolor: mw.color.skySoft, color: mw.color.sky },
}

// ---- Picker section captions (the "+" menus) ----
export const sectionLabelSx = {
  mg: { color: mw.color.emerald, fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' },
  og: { color: mw.color.amber, fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' },
  ing: { color: mw.color.sky, fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' },
}

// ---- Form section title (Edit Item: Names, Pricing, ...) ----
export const formSectionTitleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: mw.color.textMuted,
  mb: 1.5,
  '&::after': { content: '""', flex: 1, height: '1px', bgcolor: mw.color.divider },
}

// ---- Bordered option card (Multi Selecting / Required Choices / Pizza) ----
export const optionCardSx = (active, tone = 'accent') => {
  const tones = {
    accent: { bg: mw.color.accentSoft, border: mw.color.accentBorder },
    amber: { bg: '#FEF7EE', border: '#EBC9A0' },
    sky: { bg: mw.color.skySoft, border: '#A9CBE3' },
  }
  const tn = tones[tone] || tones.accent
  return {
    p: 2,
    borderRadius: mw.radius.panel,
    border: '1px solid',
    borderColor: active ? tn.border : mw.color.border,
    bgcolor: active ? tn.bg : mw.color.cardBg,
    boxShadow: active ? 'none' : '0 1px 2px rgba(23,24,30,0.03)',
    transition: 'background-color .15s ease, border-color .15s ease',
    '& .MuiFormControlLabel-root': { ml: '-6px', mr: 0, alignItems: 'center' },
  }
}

// ---- Surfaces ----
export const dialogPaperSx = { borderRadius: mw.radius.lg }

export const dialogTitleSx = {
  ...displaySx(26),
  px: 3.5,
  pt: 3,
  pb: 1,
}

export const menuPaperSx = {
  borderRadius: mw.radius.sm,
  boxShadow: mw.shadow.pop,
  border: `1px solid ${mw.color.border}`,
}

// ---- Inline price (Items list quick edit affordance) ----
export const inlinePriceSx = {
  ...numSx,
  fontSize: 14.5,
  color: mw.color.textPrimary,
  cursor: 'pointer',
  display: 'inline-block',
  borderBottom: `1px dashed ${mw.color.borderStrong}`,
  transition: 'color .15s ease, border-color .15s ease',
  '&:hover': { color: mw.color.accent, borderBottomColor: mw.color.accent },
}
